import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Get_All_Marketing_Reports_Action } from '../Redux/Actions/MarketingReports_Action';
import { GetCampainReportsData } from '../Redux/Actions/Campaign_Actions';
import { createPortal } from 'react-dom';
import {
  Bar, BarChart, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, LineChart, Line, Cell,
} from 'recharts';
import { useSidebarWidth } from '../Utility/useSidebarWidth';
import { useTheme } from '@mui/material/styles';

/* ─── palette ─────────────────────────────────────────────────────────────── */
const P = {
  bg:      '#F7F8FC',
  white:   '#FFFFFF',
  border:  '#E8EAF0',
  text:    '#1C2340',
  muted:   '#7B829A',
  accent:  '#0D47A1',
  soft:    'rgba(13,71,161,0.08)',
  success: '#12B76A',
  warning: '#F79009',
  danger:  '#F04438',
  shadow:  '0 1px 4px rgba(16,24,40,0.07)',
};

const STAGE_COLORS = [
  '#0D47A1','#1976D2','#42A5F5','#12B76A','#F79009',
  '#F04438','#8B5CF6','#EC4899','#06B6D4','#84CC16',
];

const METRICS = [
  { key:'Leads',   label:'Leads',   color:'#0D47A1' },
  { key:'Deals',   label:'Deals',   color:'#12B76A' },
  { key:'Revenue', label:'Revenue', color:'#F79009' },
  { key:'ROI',     label:'ROI',     color:'#8B5CF6' },
  { key:'CPL',     label:'CPL',     color:'#F04438' },
  { key:'CPA',     label:'CPA',     color:'#EC4899' },
];

const ITEMS_PER_PAGE = 5;

const metricVal = (r, m) => {
  switch (m) {
    case 'ROI':     return parseFloat(r.ROI) || 0;
    case 'Revenue': return r.totalRevenue    || 0;
    case 'Deals':   return r.totalDeals      || 0;
    case 'CPL':     return r.CPL             || 0;
    case 'CPA':     return r.CPA             || 0;
    case 'Leads':   return r.totalLeads      || 0;
    default:        return 0;
  }
};

/* ─── Icons ───────────────────────────────────────────────────────────────── */
const Ico = {
  mega:    () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  users:   () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  deal:    () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  dollar:  () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  percent: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>,
  close:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  star:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  layers:  () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  chevL:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>,
  chevR:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>,
  search:  () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>,
  chevD:   () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>,
};

/* ─── Custom Recharts Tooltip ─────────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:`1px solid ${P.border}`, borderRadius:10, padding:'10px 14px', boxShadow:'0 4px 16px rgba(0,0,0,0.1)', fontFamily:"'DM Sans',sans-serif" }}>
      <p style={{ margin:'0 0 4px', fontSize:11, fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin:0, fontSize:15, fontWeight:800, color:p.color || P.accent }}>
          {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

/* ─── Stat Card ───────────────────────────────────────────────────────────── */
const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:12, padding:'14px 16px', boxShadow:P.shadow, display:'flex', alignItems:'center', gap:12 }}>
    <div style={{ width:38, height:38, borderRadius:10, background:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center', color, flexShrink:0 }}>{icon}</div>
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:P.muted, letterSpacing:'0.07em', textTransform:'uppercase', marginBottom:1 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:800, color:P.text, letterSpacing:'-0.3px', fontFamily:"'DM Sans',sans-serif" }}>{value ?? '—'}</div>
    </div>
  </div>
);

/* ─── Campaign Select Dropdown ────────────────────────────────────────────── */
const CampaignSelect = ({ reports, value, onChange }) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = reports.filter(r =>
    !search || r.campaignName?.toLowerCase().includes(search.toLowerCase())
  );
  const selected = reports.find(r => r.campaignId === value);

  return (
    <div ref={ref} style={{ position:'relative', width:'100%', maxWidth:360 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:`1.5px solid ${open ? P.accent : P.border}`, borderRadius:10, background:P.white, cursor:'pointer', transition:'border-color 0.15s', userSelect:'none' }}
      >
        <div style={{ width:28, height:28, borderRadius:7, background:P.soft, display:'flex', alignItems:'center', justifyContent:'center', color:P.accent, flexShrink:0 }}>
          <Ico.mega />
        </div>
        <span style={{ flex:1, fontSize:13, fontWeight:600, color:selected ? P.text : P.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {selected ? selected.campaignName : 'All Campaigns'}
        </span>
        <span style={{ color:P.muted, transform:open?'rotate(180deg)':'none', transition:'transform 0.2s', display:'flex' }}>
          <Ico.chevD />
        </span>
      </div>

      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, right:0, zIndex:9999, background:P.white, border:`1.5px solid ${P.border}`, borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.12)', overflow:'hidden' }}>
          <div style={{ padding:'10px 12px', borderBottom:`1px solid ${P.border}`, display:'flex', alignItems:'center', gap:8, background:'#FAFBFD' }}>
            <span style={{ color:P.muted, display:'flex' }}><Ico.search /></span>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search campaign…"
              style={{ border:'none', outline:'none', background:'transparent', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:P.text, width:'100%' }}
            />
            {search && <button onClick={() => setSearch('')} style={{ border:'none', background:'none', cursor:'pointer', color:P.muted, fontSize:13 }}>✕</button>}
          </div>
          <div style={{ maxHeight:220, overflowY:'auto' }}>
            {/* All option */}
            <div
              onClick={() => { onChange(null); setOpen(false); setSearch(''); }}
              style={{ padding:'10px 14px', cursor:'pointer', fontSize:13, fontWeight:!value?700:500, color:!value?P.accent:P.text, background:!value?P.soft:'transparent', borderLeft:`3px solid ${!value?P.accent:'transparent'}` }}
            >
              All Campaigns
            </div>
            {filtered.length === 0
              ? <div style={{ padding:'14px', textAlign:'center', fontSize:12, color:P.muted }}>No campaigns found</div>
              : filtered.map(r => (
                  <div
                    key={r.campaignId}
                    onClick={() => { onChange(r.campaignId); setOpen(false); setSearch(''); }}
                    style={{ padding:'10px 14px', cursor:'pointer', fontSize:13, fontWeight:r.campaignId===value?700:500, color:r.campaignId===value?P.accent:P.text, background:r.campaignId===value?P.soft:'transparent', borderLeft:`3px solid ${r.campaignId===value?P.accent:'transparent'}`, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                    onMouseEnter={e => { if (r.campaignId!==value) e.currentTarget.style.background='#FAFBFD'; }}
                    onMouseLeave={e => { if (r.campaignId!==value) e.currentTarget.style.background='transparent'; }}
                  >
                    {r.campaignName}
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Pagination ──────────────────────────────────────────────────────────── */
const Pagination = ({ page, total, perPage, onChange }) => {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  const buildNums = () => {
    if (totalPages <= 7) return Array.from({ length:totalPages }, (_, i) => i + 1);
    if (page <= 4)                   return [1,2,3,4,5,'…',totalPages];
    if (page >= totalPages - 3)      return [1,'…',totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages];
    return [1,'…',page-1,page,page+1,'…',totalPages];
  };

  const btn = (active, disabled, content, onClick) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ minWidth:34, height:34, borderRadius:8, border:`1.5px solid ${active?P.accent:P.border}`, background:active?P.accent:P.white, color:active?'#fff':disabled?'#CBD5E1':P.muted, fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:active?700:500, cursor:disabled?'not-allowed':'pointer', padding:'0 8px', transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'center', opacity:disabled?0.45:1 }}
    >
      {content}
    </button>
  );

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 0 0', flexWrap:'wrap', gap:10 }}>
      <span style={{ fontSize:13, color:P.muted }}>
        Showing <strong style={{ color:P.text }}>{Math.min((page-1)*perPage+1,total)}–{Math.min(page*perPage,total)}</strong> of <strong style={{ color:P.text }}>{total}</strong> campaigns
      </span>
      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
        {btn(false, page===1,          '⟨|',         () => onChange(1))}
        {btn(false, page===1,          <Ico.chevL />, () => onChange(page-1))}
        {buildNums().map((n, i) =>
          n === '…'
            ? <span key={`e${i}`} style={{ padding:'0 4px', color:P.muted, fontSize:14, alignSelf:'center' }}>…</span>
            : btn(page===n, false, n, () => onChange(n))
        )}
        {btn(false, page===totalPages, <Ico.chevR />, () => onChange(page+1))}
        {btn(false, page===totalPages, '|⟩',         () => onChange(totalPages))}
      </div>
    </div>
  );
};

/* ─── Stage Breakdown Modal ───────────────────────────────────────────────── */
const StageModal = ({ report, stageData, onClose }) => {
  if (!report) return null;

  const chartData = (stageData || []).map((s, i) => ({
    name:  s.stageName,
    leads: s.leadsCount,
    fill:  STAGE_COLORS[i % STAGE_COLORS.length],
  }));
  const total = chartData.reduce((s, d) => s + d.leads, 0);
  const top   = [...chartData].sort((a, b) => b.leads - a.leads)[0];

  return createPortal(
    <div
      style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(15,23,42,0.55)', backdropFilter:'blur(8px)', padding:20, fontFamily:"'DM Sans',sans-serif" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:'#F7F8FC', borderRadius:20, width:'100%', maxWidth:740, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 32px 80px rgba(0,0,0,0.22)', border:`1px solid ${P.border}`, animation:'mrIn 0.28s cubic-bezier(0.34,1.1,0.64,1) both' }}>

        {/* header */}
        <div style={{ background:P.white, padding:'18px 22px 16px', borderBottom:`1px solid ${P.border}`, flexShrink:0, display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:P.soft, display:'flex', alignItems:'center', justifyContent:'center', color:P.accent, flexShrink:0 }}>
            <Ico.mega />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:10, fontWeight:700, color:P.muted, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:2 }}>Stage Breakdown</div>
            <h3 style={{ margin:0, fontSize:17, fontWeight:800, color:P.text, letterSpacing:'-0.3px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{report.campaignName}</h3>
          </div>
          <button onClick={onClose} style={{ width:32, height:32, borderRadius:8, border:`1.5px solid ${P.border}`, background:P.bg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:P.muted, flexShrink:0 }}>
            <Ico.close />
          </button>
        </div>

        {/* body */}
        <div style={{ flex:1, overflowY:'auto', padding:20 }}>

          {/* KPI row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
            <StatCard icon={<Ico.users />}   label="Leads"   value={report.totalLeads}         color={P.accent}  />
            <StatCard icon={<Ico.deal />}    label="Deals"   value={report.totalDeals}         color={P.success} />
            <StatCard icon={<Ico.dollar />}  label="Revenue" value={`$${report.totalRevenue}`} color={P.warning} />
            <StatCard icon={<Ico.percent />} label="ROI"     value={report.ROI}                color='#8B5CF6'   />
          </div>

          {/* chart title */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <div>
              <p style={{ margin:'0 0 1px', fontSize:10, fontWeight:700, color:P.muted, letterSpacing:'0.08em', textTransform:'uppercase' }}>Distribution</p>
              <h4 style={{ margin:0, fontSize:15, fontWeight:800, color:P.text }}>Leads per Stage</h4>
            </div>
            {top && (
              <div style={{ display:'flex', alignItems:'center', gap:5, background:P.soft, border:`1px solid ${P.border}`, padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:700, color:P.accent }}>
                <Ico.star /> Top: {top.name} ({top.leads})
              </div>
            )}
          </div>

          {chartData.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:P.muted, fontSize:14, background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
              No stage data available for this campaign.
            </div>
          ) : (
            <>
              {/* Bar Chart */}
              <div style={{ background:P.white, borderRadius:14, border:`1px solid ${P.border}`, padding:'18px 14px 12px', marginBottom:14, boxShadow:P.shadow }}>
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={chartData} barCategoryGap="38%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F8" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize:10, fill:P.muted, fontFamily:"'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize:10, fill:P.muted, fontFamily:"'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
                    <ReTooltip content={<CustomTooltip />} cursor={{ fill:'rgba(13,71,161,0.04)' }} />
                    <Bar dataKey="leads" name="Leads" radius={[6,6,0,0]} maxBarSize={52}>
                      {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* stage pills */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {chartData.map((d, i) => {
                  const pct = total > 0 ? Math.round((d.leads / total) * 100) : 0;
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8, background:P.white, border:`1.5px solid ${P.border}`, borderRadius:10, padding:'8px 12px', flex:'1 1 160px' }}>
                      <div style={{ width:9, height:9, borderRadius:3, background:d.fill, flexShrink:0 }} />
                      <span style={{ fontSize:12, fontWeight:600, color:P.text, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.name}</span>
                      <span style={{ fontSize:13, fontWeight:800, color:d.fill }}>{d.leads}</span>
                      <span style={{ fontSize:11, color:P.muted }}>({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* footer */}
        <div style={{ padding:'12px 20px', borderTop:`1px solid ${P.border}`, background:P.white, display:'flex', justifyContent:'flex-end', flexShrink:0 }}>
          <button onClick={onClose} style={{ padding:'9px 22px', borderRadius:10, border:'none', background:P.accent, color:'#fff', fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════════════════════ */
export const MarketingReportsPage = () => {
  const dispatch     = useDispatch();
  const sidebarWidth = useSidebarWidth();
  const isDark       = useTheme().palette.mode === 'dark';

  const [selectedMetric,   setSelectedMetric]   = useState('Leads');
  const [selectedReport,   setSelectedReport]   = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [page,             setPage]             = useState(1);

  useEffect(() => {
    dispatch(Get_All_Marketing_Reports_Action());
    dispatch(GetCampainReportsData());
  }, [dispatch]);

  const reports    = useSelector(s => s.AllMarketingReports.Campain_Reports) || [];
  const resalldata = useSelector(s => s.AllCampaign.campaign);

  /* stage data for modal */
  const stageData = selectedReport
    ? resalldata?.data
        ?.map(stage => {
          const match = stage.campaigns?.find(c => c.campaignId === selectedReport.campaignId);
          return match ? { stageName: stage.stageName, leadsCount: match.leadsCount } : null;
        })
        .filter(Boolean)
    : [];

  /* filtered + paginated */
  const visibleReports = selectedCampaign
    ? reports.filter(r => r.campaignId === selectedCampaign)
    : reports;

  useEffect(() => { setPage(1); }, [selectedCampaign]);

  const pageReports = visibleReports.slice((page-1)*ITEMS_PER_PAGE, page*ITEMS_PER_PAGE);

  /* chart & best */
  const activeMetric = METRICS.find(m => m.key === selectedMetric) || METRICS[0];
  const bestCampaign = [...reports].sort((a, b) => metricVal(b, selectedMetric) - metricVal(a, selectedMetric))[0];

  const lineData = reports.map(r => ({
    name:  r.campaignName?.slice(0,12) + (r.campaignName?.length > 12 ? '…' : ''),
    value: metricVal(r, selectedMetric),
  }));

  const totalLeads   = reports.reduce((s, r) => s + (r.totalLeads   || 0), 0);
  const totalDeals   = reports.reduce((s, r) => s + (r.totalDeals   || 0), 0);
  const totalRevenue = reports.reduce((s, r) => s + (r.totalRevenue || 0), 0);

  /* loading */
  if (reports.length === 0) {
    return (
      <div style={{ position:'fixed', top:50, left:sidebarWidth, right:0, bottom:0, display:'flex', alignItems:'center', justifyContent:'center', background:P.bg, flexDirection:'column', gap:12, fontFamily:"'DM Sans',sans-serif", color:P.muted }}>
        <div style={{ width:34, height:34, border:`3px solid ${P.border}`, borderTopColor:P.accent, borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
        <span style={{ fontSize:14 }}>Loading campaigns…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes mrIn{from{opacity:0;transform:translateY(16px) scale(0.98)}to{opacity:1;transform:none}}`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes spin  { to { transform:rotate(360deg); } }
        @keyframes mrIn  { from{opacity:0;transform:translateY(16px) scale(0.98)} to{opacity:1;transform:none} }
        @keyframes fadUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        .mr-card:hover { transform:translateY(-3px) !important; box-shadow:0 8px 24px rgba(13,71,161,0.13) !important; }
        .mr-stg-btn:hover { background:#0D47A1 !important; color:#fff !important; }
      `}</style>

      <div style={{ position:'fixed', top:50, left:sidebarWidth, right:0, bottom:0, overflowY:'auto', background:isDark?'#111122':P.bg, fontFamily:"'DM Sans',sans-serif", transition:'left 0.25s cubic-bezier(0.4,0,0.2,1)' }}>
        <div style={{ padding:'26px 28px 40px' }}>

          {/* ── Page Header ── */}
          <div style={{ marginBottom:22 }}>
            <p style={{ margin:'0 0 3px', fontSize:10, fontWeight:700, color:P.muted, letterSpacing:'0.1em', textTransform:'uppercase' }}>Analytics</p>
            <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800, color:isDark?'#e0e0ff':P.text, letterSpacing:'-0.4px' }}>Marketing Campaigns</h1>
            <p style={{ margin:0, fontSize:13, color:P.muted }}>Track performance, compare metrics, and explore stage breakdowns.</p>
          </div>

          {/* ── KPI Summary ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:22 }}>
            <StatCard icon={<Ico.users />}  label="Total Leads"   value={totalLeads.toLocaleString()}         color={P.accent}  />
            <StatCard icon={<Ico.deal />}   label="Total Deals"   value={totalDeals.toLocaleString()}         color={P.success} />
            <StatCard icon={<Ico.dollar />} label="Total Revenue" value={`$${totalRevenue.toLocaleString()}`} color={P.warning} />
          </div>

          {/* ── Trend Line Chart ── */}
          <div style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:16, padding:'18px 22px', marginBottom:22, boxShadow:P.shadow }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:16 }}>
              <div>
                <p style={{ margin:'0 0 1px', fontSize:10, fontWeight:700, color:P.muted, letterSpacing:'0.08em', textTransform:'uppercase' }}>Trend Analysis</p>
                <h4 style={{ margin:0, fontSize:15, fontWeight:800, color:P.text }}>
                  {activeMetric.label} across all campaigns
                  {bestCampaign && (
                    <span style={{ marginLeft:10, fontSize:11, fontWeight:700, background:`${activeMetric.color}15`, color:activeMetric.color, padding:'2px 9px', borderRadius:20 }}>
                      🏆 {bestCampaign.campaignName}
                    </span>
                  )}
                </h4>
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {METRICS.map(m => (
                  <button
                    key={m.key}
                    onClick={() => setSelectedMetric(m.key)}
                    style={{ padding:'5px 13px', borderRadius:20, border:`1.5px solid ${selectedMetric===m.key?m.color:P.border}`, background:selectedMetric===m.key?`${m.color}18`:P.bg, color:selectedMetric===m.key?m.color:P.muted, fontSize:12, fontWeight:700, cursor:'pointer', transition:'all 0.15s' }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={175}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F8" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize:10, fill:P.muted, fontFamily:"'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize:10, fill:P.muted, fontFamily:"'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
                <ReTooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="value" name={activeMetric.label} stroke={activeMetric.color} strokeWidth={2.5} dot={{ fill:activeMetric.color, r:3, strokeWidth:0 }} activeDot={{ r:5, strokeWidth:0 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ── Toolbar: Select + count ── */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0 }}>
              <CampaignSelect reports={reports} value={selectedCampaign} onChange={v => setSelectedCampaign(v)} />
              {selectedCampaign && (
                <button
                  onClick={() => setSelectedCampaign(null)}
                  style={{ padding:'9px 14px', borderRadius:10, border:`1.5px solid ${P.border}`, background:P.white, color:P.muted, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}
                >
                  Show All
                </button>
              )}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6, background:P.soft, border:`1px solid ${P.border}`, padding:'7px 14px', borderRadius:20, fontSize:12, fontWeight:700, color:P.accent, flexShrink:0 }}>
              <Ico.layers /> {visibleReports.length} campaign{visibleReports.length!==1?'s':''}
            </div>
          </div>

          {/* ── Cards Grid ── */}
          {pageReports.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 20px', color:P.muted, fontSize:14, background:P.white, borderRadius:16, border:`1px solid ${P.border}` }}>
              No campaigns found.
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))', gap:16 }}>
              {pageReports.map((report, idx) => {
                const isBest = bestCampaign?.campaignId === report.campaignId;
                const roi    = parseFloat(report.ROI) || 0;
                const roiPos = roi >= 0;

                return (
                  <div
                    key={report.campaignId || idx}
                    className="mr-card"
                    style={{ background:P.white, border:`1.5px solid ${isBest?P.accent:P.border}`, borderRadius:16, padding:'18px', boxShadow:isBest?`0 4px 16px ${P.soft}`:P.shadow, position:'relative', transition:'transform 0.18s, box-shadow 0.18s', animation:`fadUp 0.35s ${idx*0.06}s both` }}
                  >
                    {isBest && (
                      <div style={{ position:'absolute', top:-1, right:16, background:P.accent, color:'#fff', fontSize:10, fontWeight:700, padding:'3px 9px', borderRadius:'0 0 8px 8px', letterSpacing:'0.06em', display:'flex', alignItems:'center', gap:3 }}>
                        <Ico.star /> BEST
                      </div>
                    )}

                    {/* header */}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                      <div style={{ width:38, height:38, borderRadius:10, background:P.soft, display:'flex', alignItems:'center', justifyContent:'center', color:P.accent, flexShrink:0 }}>
                        <Ico.mega />
                      </div>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:700, color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{report.campaignName}</div>
                        <div style={{ fontSize:10, color:P.muted, marginTop:1 }}>ID: {report.campaignId?.slice(-8)}</div>
                      </div>
                      <div style={{ flexShrink:0, fontSize:12, fontWeight:800, color:roiPos?P.success:P.danger, background:roiPos?'rgba(18,183,106,0.1)':'rgba(240,68,56,0.08)', padding:'4px 9px', borderRadius:8 }}>
                        {roiPos?'+':''}{report.ROI}
                      </div>
                    </div>

                    {/* metrics 2-col grid */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7, marginBottom:14 }}>
                      {[
                        { label:'Leads',   value:report.totalLeads,         color:P.accent  },
                        { label:'Deals',   value:report.totalDeals,         color:P.success },
                        { label:'Revenue', value:`$${report.totalRevenue}`, color:P.warning },
                        { label:'Cost',    value:`$${report.totalCost}`,    color:P.danger  },
                        { label:'CPL',     value:`$${report.CPL}`,          color:'#8B5CF6' },
                        { label:'CPA',     value:`$${report.CPA}`,          color:'#EC4899' },
                      ].map(m => (
                        <div key={m.label} style={{ background:P.bg, borderRadius:8, padding:'7px 10px' }}>
                          <div style={{ fontSize:9, fontWeight:700, color:P.muted, letterSpacing:'0.07em', textTransform:'uppercase', marginBottom:2 }}>{m.label}</div>
                          <div style={{ fontSize:14, fontWeight:800, color:m.color }}>{m.value ?? '—'}</div>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <button
                      className="mr-stg-btn"
                      onClick={() => setSelectedReport(report)}
                      style={{ width:'100%', padding:'9px', borderRadius:10, border:`1.5px solid ${P.accent}`, background:P.soft, color:P.accent, fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 0.15s' }}
                    >
                      <Ico.layers /> View Stage Breakdown
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Pagination ── */}
          <Pagination
            page={page}
            total={visibleReports.length}
            perPage={ITEMS_PER_PAGE}
            onChange={p => { setPage(p); window.scrollTo({ top:0, behavior:'smooth' }); }}
          />

        </div>
      </div>

      {/* Stage Modal */}
      {selectedReport && (
        <StageModal
          report={selectedReport}
          stageData={stageData}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </>
  );
};
